import React, { FC, useCallback, useEffect, useState } from 'react';
import api from '../../api';
import {
  FlagHistoryRow,
  apiErrorMessage,
  describeHistoryRow,
  formatDate,
} from './platformStatsFeatureFlagTypes';

type AuthHeaders = () => Promise<{ headers: { Authorization: string } }>;

interface Props {
  flagKey: string;
  /** Bump to force a reload (after a write). */
  version: number;
  authHeaders: AuthHeaders;
}

const PAGE = 50;

/**
 * Append-only change log for one flag (`feature_flag_history`), newest first.
 * Includes direct SQL changes (actor = DB session user, no reason).
 */
const PlatformStatsFeatureFlagHistory: FC<Props> = ({
  flagKey,
  version,
  authHeaders,
}) => {
  const [rows, setRows] = useState<FlagHistoryRow[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  const fetchPage = useCallback(
    async (before?: string) => {
      const res = await api.get<{ key: string; history: FlagHistoryRow[] }>(
        `/platform-stats/feature-flags/${encodeURIComponent(flagKey)}/history`,
        {
          ...(await authHeaders()),
          params: before ? { limit: PAGE, before } : { limit: PAGE },
        },
      );
      return res.data.history;
    },
    [authHeaders, flagKey],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPage()
      .then((history) => {
        if (cancelled) return;
        setRows(history);
        setHasMore(history.length === PAGE);
      })
      .catch((err) => {
        if (!cancelled)
          setError(apiErrorMessage(err, 'Failed to load history'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchPage, version]);

  const loadMore = async () => {
    const last = rows[rows.length - 1];
    if (!last) return;
    setLoading(true);
    try {
      const more = await fetchPage(last.changedAt);
      setRows((r) => [...r, ...more]);
      setHasMore(more.length === PAGE);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load history'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className='history'>
      <h4>History</h4>
      {error && <div className='notice error'>{error}</div>}
      <div className='table-wrap'>
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Who</th>
              <th>Change</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={4} className='empty'>
                  No history yet.
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <React.Fragment key={row.id}>
                <tr
                  className={`history-row ${expanded === row.id ? 'expanded' : ''}`}
                  onClick={() =>
                    setExpanded((cur) => (cur === row.id ? null : row.id))
                  }
                >
                  <td className='when'>{formatDate(row.changedAt)}</td>
                  <td className='mono'>{row.changedBy}</td>
                  <td>
                    <span className={`chip op-${row.op.toLowerCase()}`}>
                      {row.tableName === 'feature_flag_rules' ? 'rule' : 'flag'}{' '}
                      {row.op.toLowerCase()}
                    </span>{' '}
                    {describeHistoryRow(row)}
                  </td>
                  <td>
                    {row.reason ?? (
                      <span className='muted'>— (direct SQL)</span>
                    )}
                  </td>
                </tr>
                {expanded === row.id && (
                  <tr className='history-detail'>
                    <td colSpan={4}>
                      <div className='diff'>
                        <div>
                          <strong>Before</strong>
                          <pre>{JSON.stringify(row.rowBefore, null, 2)}</pre>
                        </div>
                        <div>
                          <strong>After</strong>
                          <pre>{JSON.stringify(row.rowAfter, null, 2)}</pre>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {hasMore && (
        <button
          type='button'
          className='ghost-btn'
          disabled={loading}
          onClick={() => void loadMore()}
        >
          {loading ? 'Loading…' : 'Load older'}
        </button>
      )}
    </section>
  );
};

export default PlatformStatsFeatureFlagHistory;
